/**
 * One question, as the founder sees it.
 *
 * ask mode  = interactive card (deck detail, open area questions)
 * read mode = compact row, tappable to open the detail
 */

import { useState } from "react";
import {
  answerSummary,
  isSnoozed,
  recommendedKey,
  statusWord,
  type AnswerPatch,
  type Question,
} from "../lib/questions";
import { SnoozeSheet } from "./SnoozeSheet";

export function QuestionCard({
  q,
  busy,
  staged,
  onAnswer,
  onSnooze,
  onOpen,
  mode = "ask",
}: {
  q: Question;
  busy?: boolean;
  staged?: boolean;
  onAnswer?: (patch: AnswerPatch) => void;
  onSnooze?: (until: string) => void;
  onOpen?: () => void;
  mode?: "ask" | "read";
}) {
  const [showWhy, setShowWhy] = useState(false);
  const [showFree, setShowFree] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [showSnooze, setShowSnooze] = useState(false);
  const [neverArmed, setNeverArmed] = useState(false);
  const recKey = recommendedKey(q);
  const rec = q.options.find((o) => o.key === recKey);
  const summary = answerSummary(q);
  const word = statusWord(q);
  const snoozed = q.status === "open" && isSnoozed(q);

  if (mode === "read") {
    return (
      <button
        onClick={onOpen}
        disabled={!onOpen}
        className={`card w-full p-3 text-left ${onOpen ? "hover:bg-ink-850" : ""}`}
      >
        <div className="mb-1 flex items-center gap-2">
          <span
            className={`chip shrink-0 ${
              word === "Open"
                ? "bg-accent/15 text-accent-soft"
                : word === "Snoozed"
                  ? "bg-warn/15 text-warn"
                  : "bg-ink-800 text-ink-400"
            }`}
          >
            {word}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-200">
            {q.title}
          </span>
          {q.created && (
            <span className="shrink-0 text-[11px] text-ink-600">{q.created}</span>
          )}
        </div>
        {summary && <div className="text-xs text-ink-400">{summary}</div>}
        {snoozed && (
          <div className="text-xs text-warn">Snoozed until {q.snoozeUntil}</div>
        )}
      </button>
    );
  }

  return (
    <div className="card relative p-4">
      {busy && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-ink-950/70 text-sm text-accent-soft">
          syncing…
        </div>
      )}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="chip bg-ink-850 text-ink-400">
          {q.domain}
          {q.area ? ` · ${q.area}` : ""}
        </span>
        {q.created && (
          <span className="chip bg-ink-850 text-ink-500">created {q.created}</span>
        )}
        <span
          className={`chip ${
            word === "Open"
              ? "bg-accent/15 text-accent-soft"
              : word === "Snoozed"
                ? "bg-warn/15 text-warn"
                : "bg-ink-800 text-ink-400"
          }`}
        >
          {word}
        </span>
        {staged && <span className="chip bg-warn/15 text-warn">staged</span>}
        {q.links.length > 0 && (
          <span className="chip bg-ink-850 text-ink-500">📄 {q.links.length}</span>
        )}
      </div>

      <h2 className="text-[17px] font-semibold leading-snug text-ink-100">{q.title}</h2>

      {q.whyNow && (
        <button
          onClick={() => setShowWhy((v) => !v)}
          className="mt-2 w-full text-left text-sm leading-relaxed text-ink-400"
        >
          <span className={showWhy ? "" : "line-clamp-3"}>{q.whyNow}</span>
          <span className="mt-1 block text-xs text-ink-500">
            {showWhy ? "show less" : "why this matters now"}
          </span>
        </button>
      )}

      {q.context && (
        <div className="mt-3 rounded-xl border border-ink-800 bg-ink-850/60 p-3">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-500">
            Context
          </div>
          <p className="text-sm leading-relaxed text-ink-300">{q.context}</p>
        </div>
      )}

      {snoozed && onSnooze && (
        <div className="mt-3 flex items-center justify-between rounded-xl border border-warn/30 bg-warn/10 p-3">
          <span className="text-sm text-warn">Snoozed until {q.snoozeUntil}</span>
          <button
            className="btn-ghost px-3 py-1.5 text-xs"
            onClick={() => onSnooze("")}
          >
            Bring back now
          </button>
        </div>
      )}

      {q.status === "open" && !snoozed && rec && onAnswer && (
        <div className="mt-3 rounded-xl border border-accent/30 bg-accent/5 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-accent-soft">
            Recommendation
          </div>
          <div className="mt-1 text-sm leading-snug text-ink-200">
            {rec.key} — {rec.label}
          </div>
          <button
            className="btn-primary mt-2 w-full py-2 text-xs"
            onClick={() => onAnswer({ choice: rec.key })}
          >
            Take recommendation
          </button>
        </div>
      )}

      {q.status === "open" && onAnswer && (
        <div className="mt-3 space-y-2">
          {q.options.map((o) => {
            const isOwn = /^X$/i.test(o.key) || /something else/i.test(o.label);
            return (
              <button
                key={o.key}
                onClick={() => (isOwn ? setShowFree(true) : onAnswer({ choice: o.key }))}
                className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-ink-850 ${
                  o.key === recKey ? "border-accent/30" : "border-ink-800"
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="chip mt-0.5 bg-ink-800 text-ink-300">{o.key}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-snug text-ink-200">
                      {o.label}
                    </span>
                    {o.consequence && (
                      <span className="mt-1 block text-xs leading-relaxed text-ink-500">
                        {o.consequence}
                      </span>
                    )}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {q.status === "open" && onAnswer && showFree && (
        <div className="mt-3">
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            rows={3}
            placeholder="Your own answer, in your words…"
            className="w-full rounded-xl border border-ink-700 bg-ink-850 p-3 text-sm text-ink-200 placeholder:text-ink-500 focus:border-accent/50 focus:outline-none"
          />
          <button
            className="btn-primary mt-2 w-full py-2 text-xs disabled:opacity-40"
            disabled={!freeText.trim()}
            onClick={() => onAnswer({ text: freeText.trim() })}
          >
            Send
          </button>
        </div>
      )}

      {q.status === "open" && (onAnswer || onSnooze) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {onAnswer && (
            <button
              className="btn-ghost flex-1 py-2 text-xs"
              onClick={() => onAnswer({ delegated: true })}
            >
              Let the recommendation stand
            </button>
          )}
          {onSnooze && !snoozed && (
            <button
              className="btn-ghost flex-1 py-2 text-xs"
              onClick={() => setShowSnooze(true)}
            >
              Snooze
            </button>
          )}
          {onAnswer && (
            <button
              className={`btn-ghost py-2 text-xs ${neverArmed ? "border-danger/50 text-danger" : ""}`}
              onClick={() => {
                if (!neverArmed) {
                  setNeverArmed(true);
                  window.setTimeout(() => setNeverArmed(false), 3000);
                  return;
                }
                onAnswer({ waived: true });
              }}
            >
              {neverArmed ? "Tap again: never ask" : "Never"}
            </button>
          )}
          {onAnswer && !showFree && (
            <button className="btn-ghost py-2 text-xs" onClick={() => setShowFree(true)}>
              Own words…
            </button>
          )}
        </div>
      )}

      {q.status !== "open" && summary && (
        <div className="mt-3 rounded-xl border border-ink-800 bg-ink-850/60 p-3 text-sm text-ink-300">
          {summary}
        </div>
      )}

      {showSnooze && onSnooze && (
        <SnoozeSheet
          onPick={(until) => {
            setShowSnooze(false);
            onSnooze(until);
          }}
          onClose={() => setShowSnooze(false)}
        />
      )}
    </div>
  );
}
